<template>
  <div class="p-6">
    <h2 class="mb-4">Assessment Schemas Manager</h2>
    <div class="mb-4">
      <Button label="Create New Schema" icon="pi pi-plus" @click="openDialog" />
    </div>
    <Dialog
      header="Create New Assessment Schema"
      v-model:visible="createDialog"
      modal
      @hide="onCreateDialogHide"
      class="w-1/4"
    >
      <NewSchemaDialog
        :visible="createDialog"
        @update:visible="createDialog = $event"
        @create-schema="handleCreateSchema"
      />
    </Dialog>
    <div class="p-4">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Class
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Type
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Tags
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Question Rotation
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="schema in assessmentSchemas" :key="schema._id">
            <td class="px-6 py-4 whitespace-nowrap">
              {{ schema.class }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="inline-block bg-orange-200 text-gray-800 px-2 py-1 rounded mr-1">
                {{ schema.type }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                v-for="tag in schema.tags"
                :key="tag"
                class="inline-block bg-gray-200 text-gray-800 px-2 py-1 rounded mr-1"
              >
                {{ tag }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <!-- Question Rotation Status -->
              <!-- Debug: Uncomment to see what data we're getting -->
              <!-- {{ JSON.stringify({
                enabled: getQuestionRotationEnabled(schema),
                rotated: schema.rotatedQuestionsSchema,
                config: schema.config
              }) }} -->
              <div v-if="getQuestionRotationEnabled(schema)" class="flex items-center space-x-2">
                <span class="inline-block bg-green-200 text-green-800 px-2 py-1 rounded text-xs">
                  <i class="pi pi-refresh mr-1"></i>
                  {{ getQuestionRotationPeriod(schema) }}
                </span>
                <span class="text-xs text-gray-500">
                  {{ getTotalQuestions(schema) }} questions
                </span>
              </div>
              <div v-else class="text-xs text-gray-400">
                Disabled
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <!-- Edit Button -->
              <Button
                label="Edit"
                icon="pi pi-pen-to-square"
                class="mr-2"
                @click="goToSchemaDetailsView(schema)"
              />
              <!-- Dropdown toggle button -->
              <Button
                icon="pi pi-ellipsis-v"
                class="p-button-rounded"
                @click="toggleMenu($event, schema)"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <!-- Dropdown Menu (popup) for additional actions -->
      <Menu :model="menuItems" popup ref="menu" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import Dialog from 'primevue/dialog';
import NewSchemaDialog from '../components/NewSchemaDialog.vue';

export default {
  name: 'AssessmentSchemasTable',
  components: {
    Dialog,
    Button,
    Menu,
    NewSchemaDialog,
  },
  computed: {
    ...mapState('assessmentSchemas', ['assessmentSchemas']),
    ...mapGetters('assessmentSchemas', [
      'getQuestionRotationEnabled',
      'getQuestionRotationPeriod',
      'getTotalQuestions',
    ]),

    // Define the menu items for the dropdown. Actions will use the currentSchema.
    menuItems() {
      return [
        {
          label: 'Delete',
          icon: 'pi pi-trash',
          command: () => {
            this.deleteAssessmentSchema();
          },
        },
        {
          label: 'Info',
          icon: 'pi pi-info-circle',
          command: () => {
            this.infoTask();
          },
        },
      ];
    },
  },
  data() {
    return {
      createDialog: false,
      // Holds the task for which the menu was opened.
      currentSchema: null,
    };
  },
  mounted() {
    this.fetchSchemas();
    this.fetchEnumTypes();
  },
  methods: {
    ...mapActions('enums', ['fetchEnumTypes']),
    async fetchSchemas() {
      await this.$store.dispatch('assessmentSchemas/fetchAssessments');
      this.schemas = this.$store.state.assessmentSchemas.assessments;
    },
    goToSchemaDetailsView(assessmentSchema) {
      this.$router.push({ name: 'AssessmentDetails', params: { id: assessmentSchema._id } });
    },
    toggleMenu(event, task) {
      this.currentSchema = task;
      this.$refs.menu.toggle(event);
    },
    async deleteAssessmentSchema() {
      if (this.currentSchema) {
        await this.$store.dispatch('assessmentSchemas/deleteAssessment', this.currentSchema._id);
      }
    },
    infoTask() {
      // Navigate to a detailed info view or open a modal.
      if (this.currentSchema) {
        this.$router.push({ name: 'TaskInfo', params: { id: this.currentSchema.id } });
      }
    },
    openDialog() {
      this.createDialog = true;
    },
    onCreateDialogHide() {
      this.createDialog = false;
    },
    async handleCreateSchema(payload) {
      try {
        await this.$store.dispatch('assessmentSchemas/createAssessment', payload);
        this.createDialog = false;
      } catch {
        // Store already toasts (including 409 duplicate type). Keep the dialog open.
      }
    },
  },
};
</script>

<style scoped>
/* Additional styling if necessary */
</style>
